// Parse the `detail` JSON blob on an activity log entry into a small,
// renderable summary string. The backend writes a free-form JSON object;
// we recognise the common shapes ({from, to} for status/price changes)
// and fall back to "raw string passes through" so unknown actions still
// render something rather than nothing.

import { formatPrice } from "@/features/property/utils/format-price";
import type { ListingType } from "@/types/property";

export interface ParsedActivityDetail {
  /** A short rendered summary; null when nothing useful was found. */
  summary: string | null;
  /** True when the detail describes a from→to transition. */
  isTransition: boolean;
}

function safeJson(raw: string | null): Record<string, unknown> | null {
  if (raw === null || raw.length === 0) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Not JSON.
  }
  return null;
}

/**
 * Produce a Vietnamese-friendly rendering of the activity detail blob.
 * Hooks the action code to its known shape; anything else passes the
 * raw string through (useful while the backend stabilises).
 */
export function formatActivityDetail(
  action: string,
  detail: string | null,
  listingType: ListingType = "SALE",
): ParsedActivityDetail {
  const obj = safeJson(detail);
  if (obj !== null) {
    switch (action) {
      case "PRICE_CHANGED": {
        const from =
          typeof obj.from === "number" ? formatPrice(obj.from, listingType) : null;
        const to =
          typeof obj.to === "number" ? formatPrice(obj.to, listingType) : null;
        if (from !== null && to !== null) {
          return { summary: `${from} → ${to}`, isTransition: true };
        }
        break;
      }
      case "STATUS_CHANGED":
      case "LEAD_STATUS_CHANGED":
      case "APPOINTMENT_STATUS_CHANGED": {
        const from = typeof obj.from === "string" ? obj.from : null;
        const to = typeof obj.to === "string" ? obj.to : null;
        if (from !== null && to !== null) {
          return { summary: `${from} → ${to}`, isTransition: true };
        }
        break;
      }
      case "INFO_UPDATED":
      case "CUSTOMER_UPDATED": {
        if (Array.isArray(obj.fields)) {
          const cleaned = (obj.fields as unknown[]).filter(
            (v): v is string => typeof v === "string",
          );
          if (cleaned.length > 0) {
            return { summary: cleaned.join(", "), isTransition: false };
          }
        }
        break;
      }
    }
  }
  if (detail && obj === null) {
    return { summary: detail, isTransition: false };
  }
  return { summary: null, isTransition: false };
}
